import { useEffect } from "react";
import * as THREE from "three";
import { setConstraintVisualizationUpdater } from "../../runtime/constraintVisualizationBridge";
import {
  CONNECT_CONSTRAINT_GAP_VISIBLE_METERS,
  CONNECT_CONSTRAINT_GAP_WARN_METERS,
  CONSTRAINT_CONNECT_BODY1_MARKER_COLOR,
  CONSTRAINT_CONNECT_BODY2_MARKER_COLOR,
  CONSTRAINT_CONNECT_MARKER_COLOR,
} from "../scene/sceneConstants";
import {
  getConnectBodyAnchorState,
  getConnectBodyAnchorWorldPositions,
  getConnectBodyDisplayLinePoints,
} from "../scene/constraintMarkerGeometry";
import { getConstraintMarkerSettingsScales } from "../scene/jointMarkerSizing";
import { dispatchCritterToast } from "../scene/sceneMisc";
import { logger } from "../../utils/logger";

export function useConstraintVisualizationBridge({
  sceneRef,
  sceneObjectsRef,
  constraintVisualizationRef,
  missingConstraintBodyToastKeysRef,
  constraintMarkerSettings,
  constraintMarkerLayerVisible,
  selectedEquality,
}) {
  // Note: Connect constraints are only enforced in physics mode.
  // In kinematics mode, they require solving inverse kinematics for closed kinematic loops,
  // which is complex and mechanism-specific. For now, connect constraints only work when physics is enabled.

  // Constraint visualization - set up function as soon as scene is available
  useEffect(() => {
    if (!sceneRef.current) {
      // Scene not ready yet, set up a placeholder function that will be replaced.
      setConstraintVisualizationUpdater(() => {
        logger.warn("[Scene] Constraint visualization called before scene is ready");
      });
      return () => {
        setConstraintVisualizationUpdater(null);
      };
    }

    // Create or get constraint visualization group - add to scene directly so it renders on top
    let constraintGroup = constraintVisualizationRef.current;
    if (!constraintGroup && sceneRef.current) {
      constraintGroup = new THREE.Group();
      constraintGroup.name = "constraint_visualization";
      constraintGroup.visible = true; // Ensure it's visible
      // Add to scene root so it renders on top of everything
      sceneRef.current.add(constraintGroup);
      constraintVisualizationRef.current = constraintGroup;
    }

    // Expose update function via explicit runtime bridge.
    // Use a closure to capture the current constraint group.
    const updateConstraints = (constraints) => {
      // Get the current constraint group (it might have been recreated)
      const currentGroup = constraintVisualizationRef.current;

      if (!currentGroup) {
        logger.warn("[Scene] Constraint group not available, creating new one");
        if (sceneRef.current) {
          const newGroup = new THREE.Group();
          newGroup.name = "constraint_visualization";
          sceneRef.current.add(newGroup);
          constraintVisualizationRef.current = newGroup;
          // Recursively call with the new group
          updateConstraints(constraints);
          return;
        } else {
          logger.warn("[Scene] Scene not available, cannot create constraint group");
          return;
        }
      }

      const constraintGroup = currentGroup;

      // Clear existing visualization (including spheres attached to bodies)
      while (constraintGroup.children.length > 0) {
        const child = constraintGroup.children[0];
        // Clean up any stored sphere references
        if (child.userData && child.userData.sphere1) {
          try {
            child.userData.sphere1.parent?.remove(child.userData.sphere1);
          } catch (error) {
            logger.warn("[Scene] Unexpected error during scene update", error);
          }
        }
        if (child.userData && child.userData.sphere2) {
          try {
            child.userData.sphere2.parent?.remove(child.userData.sphere2);
          } catch (error) {
            logger.warn("[Scene] Unexpected error during scene update", error);
          }
        }
        constraintGroup.remove(child);
      }

      const allowConstraintVisualization =
        constraintMarkerLayerVisible ||
        (typeof selectedEquality === "string" && !!selectedEquality);
      constraintGroup.visible = allowConstraintVisualization;
      if (!allowConstraintVisualization || !constraints || constraints.length === 0) {
        return;
      }

      constraints.forEach((constraint, idx) => {
        if (!constraintMarkerLayerVisible && constraint.uuid !== selectedEquality) return;
        if (constraint.type !== "connect" || constraint.objtype !== "body") {
          return;
        }
        // Use UUIDs (obj1, obj2) instead of names
        if (!constraint.obj1 || !constraint.obj2) {
          return;
        }

        const body1Obj = sceneObjectsRef.current[constraint.obj1];
        const body2Obj = sceneObjectsRef.current[constraint.obj2];

        if (!body1Obj || !body2Obj) {
          logger.warn(`[Scene] Bodies not found for constraint ${idx}:`, {
            obj1: constraint.obj1,
            obj2: constraint.obj2,
            availableBodies: Object.keys(sceneObjectsRef.current || {}),
          });
          const key =
            constraint.uuid ||
            `${constraint.obj1 || "missing"}:${constraint.obj2 || "missing"}:${idx}`;
          if (!missingConstraintBodyToastKeysRef.current.has(key)) {
            missingConstraintBodyToastKeysRef.current.add(key);
            dispatchCritterToast("Constraint references a deleted body and cannot be visualized");
          }
          return;
        }

        const anchor =
          constraint.data && Array.isArray(constraint.data) && constraint.data.length >= 3
            ? constraint.data
            : [0, 0, 0];

        // MuJoCo body-connect anchors are specified in body1's local frame.
        // The matching body2 point is compiler-derived from the model pose.
        const anchorState = getConnectBodyAnchorState(body1Obj, body2Obj, anchor);
        const worldPos1 = anchorState.body1World;
        const worldPos2 = anchorState.body2World;

        const constraintMarkerScales = getConstraintMarkerSettingsScales(constraintMarkerSettings);

        // Create anchor point spheres
        const sphereGeometry = new THREE.SphereGeometry(
          0.01 * constraintMarkerScales.markerScale,
          16,
          16
        );
        const sphere2Geometry = new THREE.SphereGeometry(
          0.014 * constraintMarkerScales.markerScale,
          8,
          4
        );
        const sphereMaterial = new THREE.MeshBasicMaterial({
          color: CONSTRAINT_CONNECT_BODY1_MARKER_COLOR,
          transparent: false,
          opacity: 1.0,
          depthTest: true,
          depthWrite: true,
          side: THREE.DoubleSide,
        });

        const eqUuid = constraint.uuid;
        const sphere1Group = new THREE.Group();
        sphere1Group.position.copy(worldPos1);
        const sphere1 = new THREE.Mesh(sphereGeometry, sphereMaterial.clone());
        sphere1.material.visible = constraintMarkerScales.anchorSpheresVisible;
        sphere1.renderOrder = 999;
        sphere1.userData.isConstraintVisualization = true;
        sphere1Group.add(sphere1);
        sphere1Group.userData.isConstraintVisualization = true;
        sphere1Group.userData.key = eqUuid;
        constraintGroup.add(sphere1Group);

        const sphere2Group = new THREE.Group();
        sphere2Group.position.copy(worldPos2);
        const sphere2Material = sphereMaterial.clone();
        sphere2Material.color.setHex(CONSTRAINT_CONNECT_BODY2_MARKER_COLOR);
        sphere2Material.wireframe = true;
        const sphere2 = new THREE.Mesh(sphere2Geometry, sphere2Material);
        sphere2.material.visible = constraintMarkerScales.anchorSpheresVisible;
        sphere2.renderOrder = 1000;
        sphere2.userData.isConstraintVisualization = true;
        sphere2Group.add(sphere2);
        sphere2Group.userData.isConstraintVisualization = true;
        sphere2Group.userData.key = eqUuid;
        constraintGroup.add(sphere2Group);

        // Line connecting the two anchor points; color updates dynamically in the animation loop
        const lineMaterial = new THREE.LineBasicMaterial({
          color: CONSTRAINT_CONNECT_MARKER_COLOR,
          transparent: false,
          opacity: 1.0,
          linewidth: 5 * constraintMarkerScales.lineWidthScale,
          depthTest: true,
          depthWrite: true,
        });
        const lineGeometry = new THREE.BufferGeometry();
        const displayLine = getConnectBodyDisplayLinePoints(
          {
            body1: body1Obj,
            body2: body2Obj,
            body1Anchor: anchorState.body1Anchor,
            body2Anchor: anchorState.body2Anchor,
          },
          {
            body1World: worldPos1,
            body2World: worldPos2,
          }
        );
        const linePoints = displayLine?.points || [worldPos1, worldPos2];
        lineGeometry.setFromPoints(linePoints);
        const line = new THREE.Line(lineGeometry, lineMaterial);
        line.renderOrder = 999; // Render on top
        line.userData.isConstraintVisualization = true; // Mark to skip visibility updates
        constraintGroup.add(line);

        // Mark the constraint group itself
        constraintGroup.userData.isConstraintVisualization = true;

        // Store references for animation updates (preserve the isConstraintVisualization flag)
        line.userData = {
          ...line.userData, // Preserve existing userData (including isConstraintVisualization)
          key: constraint.uuid,
          body1: body1Obj,
          body2: body2Obj,
          body1Anchor: anchorState.body1Anchor,
          body2Anchor: anchorState.body2Anchor,
          sphere1Group: sphere1Group,
          sphere2Group: sphere2Group,
        };
      });

      // Ensure constraint group is visible and in scene
      if (constraintGroup.parent !== sceneRef.current && sceneRef.current) {
        if (constraintGroup.parent) {
          constraintGroup.parent.remove(constraintGroup);
        }
        sceneRef.current.add(constraintGroup);
      }
      constraintGroup.visible = true;

      // Force all children to be visible
      constraintGroup.children.forEach((child) => {
        child.visible = true;
        if (child.children) {
          child.children.forEach((grandchild) => {
            grandchild.visible = true;
          });
        }
      });
    };
    setConstraintVisualizationUpdater(updateConstraints);

    const _constraintGreen = new THREE.Color(CONSTRAINT_CONNECT_MARKER_COLOR);
    const _constraintRed = new THREE.Color(0xff2200);

    // Animation loop to update line and sphere positions
    const updateConstraintVisualization = () => {
      if (!constraintGroup) return;

      constraintGroup.children.forEach((obj) => {
        if (!obj.userData) return;

        const { sphere1Group, sphere2Group } = obj.userData;
        const positions = getConnectBodyAnchorWorldPositions(obj.userData);
        if (!positions) return;
        const { body1World: worldPos1, body2World: worldPos2 } = positions;
        const gap = worldPos1.distanceTo(worldPos2);

        if (sphere1Group) sphere1Group.position.copy(worldPos1);
        if (sphere2Group) sphere2Group.position.copy(worldPos2);

        if (obj.type === "Line") {
          const displayLine = getConnectBodyDisplayLinePoints(obj.userData, positions);
          const linePoints = displayLine?.points || [worldPos1, worldPos2];
          obj.geometry.setFromPoints(linePoints);
          obj.geometry.attributes.position.needsUpdate = true;
          obj.visible =
            linePoints[0].distanceTo(linePoints[1]) > CONNECT_CONSTRAINT_GAP_VISIBLE_METERS;
          // Color shifts green -> red as the constraint gap grows.
          const t = displayLine?.isAuthoredSpan
            ? 0
            : Math.min(gap / CONNECT_CONSTRAINT_GAP_WARN_METERS, 1.0);
          obj.material.color.lerpColors(_constraintGreen, _constraintRed, t);
        }
      });
    };

    // Update visualization on each frame
    let rafId = null;
    const animate = () => {
      updateConstraintVisualization();
      rafId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      setConstraintVisualizationUpdater(null);
      if (rafId !== null) cancelAnimationFrame(rafId);
      // Clean up visualization group
      if (constraintGroup && constraintGroup.parent) {
        constraintGroup.parent.remove(constraintGroup);
      }
    };
  }, [
    sceneObjectsRef,
    sceneRef,
    constraintMarkerSettings,
    constraintMarkerLayerVisible,
    selectedEquality,
    constraintVisualizationRef,
    missingConstraintBodyToastKeysRef,
  ]); // Removed modelGroupRef dependency since we don't need it
}
